import { Box, Callout, Tag } from '@dev-spendesk/grapes';
import { formatDate } from 'date-fns';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useCompanyCurrency } from 'src/core/modules/app/hooks/useCompanyCurrency';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { selectors } from 'src/core/modules/invoices/transfer';
import { useFetchSuppliers } from 'src/core/modules/suppliers/hooks/api/useFetchSupplier';
import { isInternationalTransfer } from 'src/core/utils';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { LoadingQuoteBreakdown } from './LoadingQuoteBreakdown';
import { useQuotes, VALIDATION_ERROR_CAUSE } from '../../hooks/api';

type QuoteBreakdownProps = {
  showTotalAmountInline?: boolean;
  isSchedulePage?: boolean;
};

function formatRate({
  rate,
  targetCurrency,
  sourceCurrency,
}: {
  rate: number;
  targetCurrency: string;
  sourceCurrency: string;
}): string {
  return `1 ${sourceCurrency} = ${rate.toPrecision(6)} ${targetCurrency}`;
}

function WiseServiceLabel() {
  return (
    <div className="flex flex-row">
      <p className="text-sm italic">Services provided by Wise</p>
    </div>
  );
}

export const QuoteBreakdown = ({
  isSchedulePage = false,
  showTotalAmountInline,
}: QuoteBreakdownProps) => {
  const { t } = useTranslation('global');

  const companyId = useCompanyId();

  const companyCurrency = useCompanyCurrency();

  const selectedTransfers = useSelector(
    selectors.selectTransfersByType(isSchedulePage),
  );

  const [validationError, setValidationError] = useState<string>('');

  const { quotes, totalAmountWithQuotes, totalFees, isLoading } = useQuotes({
    companyId,
    selectedTransfers,
    companyCurrency,
    onError: (error) => {
      if (error instanceof Error && error.cause === VALIDATION_ERROR_CAUSE) {
        setValidationError(error.message);
      }
    },
  });

  const suppliers = useFetchSuppliers(
    selectedTransfers.map((transfer) => transfer.bill.counterparty.id),
  );
  if (suppliers.some((supplier) => supplier.status !== 'success')) {
    return null;
  }

  // Don't render if all selected transfers are non cross-currency.
  const hasForeignCurrencyTransfers = selectedTransfers.some((transfer) => {
    const selectedTransferSupplier = suppliers.find((supplier) => {
      return (
        supplier.status === 'success' &&
        supplier.data.id === transfer.bill.counterparty.id
      );
    });

    return isInternationalTransfer({
      sourceCurrency: companyCurrency,
      targetCurrency: transfer.amount.currency,
      targetCountry:
        selectedTransferSupplier?.data?.supplier_details?.bank_country ??
        undefined,
      iban: transfer.bill.counterparty.bankAddress?.iban,
    });
  });

  if (quotes.length === 0) {
    return null;
  }

  if (!hasForeignCurrencyTransfers) {
    return null;
  }

  if (isLoading) {
    return <LoadingQuoteBreakdown />;
  }

  if (validationError) {
    return <Callout title={validationError} variant="alert" />;
  }

  const ratesByCurrency = quotes.reduce<Record<string, number>>(
    (accumulator, value) => {
      if (value.targetCurrency && !accumulator[value.targetCurrency]) {
        accumulator[value.targetCurrency] = value.rate;
      }
      return accumulator;
    },
    {},
  );

  const estimatedFormatTimeByCurrency = quotes.reduce<Record<string, string>>(
    (accumulator, value) => {
      if (
        value.paymentOptions.length &&
        value.paymentOptions[0].estimatedDeliveryDate &&
        !accumulator[value.targetCurrency]
      ) {
        accumulator[value.targetCurrency] = formatDate(
          new Date(value.paymentOptions[0].estimatedDeliveryDate),
          'PPpp',
        );
      }
      return accumulator;
    },
    {},
  );

  return (
    <Box variant="secondary">
      <div className="flex flex-col gap-16">
        <h3 className="text-content-primary title-l text-start">
          {t('quoteBreakdown.paymentDetails')} <Tag variant="ocean">New!</Tag>
        </h3>

        {/* Display the disabled quotes reason message */}
        {isSchedulePage &&
          quotes
            .filter((it) => it.paymentOptions.length > 0)
            .filter((it) => it.paymentOptions[0].disabledReason)
            .map((it) => (
              <Callout
                key={it.id}
                variant="alert"
                title={t('quoteBreakdown.quoteDisabledReason', {
                  reason: it.paymentOptions[0].disabledReason?.message,
                })}
              />
            ))}

        <div className="flex justify-between">
          <p className="text-content-primary body-m">
            {t('quoteBreakdown.currentExchangeRates')}
          </p>

          <div className="flex flex-col items-end gap-12">
            {Object.entries(ratesByCurrency).map(([currency, rate]) => {
              return (
                <p
                  className="text-content-primary body-m"
                  key={`unit-currency-breakdown-${currency}`}
                >
                  {formatRate({
                    sourceCurrency: companyCurrency,
                    targetCurrency: currency,
                    rate,
                  })}
                </p>
              );
            })}
          </div>
        </div>

        <div className="flex justify-between">
          <p className="text-content-primary body-m">Fees</p>
          <p className="text-content-primary body-m">
            {formatMonetaryValue(totalFees)}
          </p>
        </div>

        {showTotalAmountInline ? (
          <div className="flex justify-between">
            <p className="text-content-primary body-l">
              {t('quoteBreakdown.totalOutgoingAmount')}
            </p>
            <p className="text-content-primary body-l">
              {formatMonetaryValue(totalAmountWithQuotes)}
            </p>
          </div>
        ) : null}

        {!isSchedulePage
          ? Object.entries(estimatedFormatTimeByCurrency).map(
              ([currency, estimatedFormattedTime]) => {
                return (
                  <p
                    className="text-content-secondary-bg-primary body-s text-end"
                    key={`estimated-time-${currency}`}
                  >
                    {t('quoteBreakdown.estimatedTime', {
                      estimatedTime: estimatedFormattedTime,
                      currency,
                    })}
                  </p>
                );
              },
            )
          : null}

        {companyCurrency === 'GBP' ? <WiseServiceLabel /> : null}
      </div>
    </Box>
  );
};
